/*
 * My Matches — dedicated, simplified screen.
 *
 * Route: /app/tournaments/:eventId/my-matches
 *
 * UX shape Travis asked for:
 *   1. Top of page: dropdown to pick the round you're playing.
 *   2. The selected round's match (yours) shows below: who you're
 *      partnered with, who you're playing against, and current state.
 *   3. One button — "Enter scores" — that drops you straight into
 *      the hole-by-hole score entry page.
 *
 * If the user isn't in any matches yet, we show a friendly empty
 * state rather than an empty dropdown. If the event only has one
 * match for them, we still render the dropdown (locked to that
 * round) so the layout doesn't shift.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Check, ChevronDown, Play, Users } from 'lucide-react';

import { api } from '@/lib/api';
import { AppShell } from '@/components/AppShell';
import { useAuthStore } from '@/store/authStore';

type Participant = {
  matchId: string;
  eventPlayerId: string;
  teamId: string | null;
  sideNumber: number;
  playingHandicap: number | null;
  displayName: string;
  userId: string | null;
};

type MyMatch = {
  matchId: string;
  matchNumber: number;
  roundId: string;
  roundName: string;
  roundNumber: number;
  roundStatus: string;
  sessionType: string | null;
  formatType: string;
  status: string;
  teamAId: string | null;
  teamBId: string | null;
  finalResultDisplay: string | null;
  participants: Participant[];
};

function formatHandicap(value: number | null | undefined): string {
  if (value == null) return '—';
  const abs = Math.round(Math.abs(value) * 10) / 10;
  const formatted = Number.isInteger(abs) ? String(abs) : abs.toFixed(1);
  return value < 0 ? `+${formatted}` : formatted;
}

function sideSummary(players: Participant[], fallback: string): string {
  return players.map((p) => `HCP ${formatHandicap(p.playingHandicap)} ${p.displayName}`).join(' / ') || fallback;
}

const FORMAT_LABEL: Record<string, string> = {
  singles_match_play:       'Singles',
  foursomes_alternate_shot: 'Foursomes',
  four_ball_better_ball:    'Four-Ball',
  scramble_match_play:      'Scramble',
  shamble_match_play:       'Shamble',
  best_ball:                'Best Ball',
};

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  draft:                 { label: 'Not started',     color: 'var(--text-mut)' },
  published:             { label: 'Ready',           color: 'var(--text-sec)' },
  ready:                 { label: 'Ready',           color: 'var(--text-sec)' },
  in_progress:           { label: 'In progress',     color: 'var(--green-bright)' },
  pending_confirmation:  { label: 'Pending confirm', color: 'var(--gold)' },
  final:                 { label: 'Final',           color: 'var(--text)' },
  disputed:              { label: 'Disputed',        color: 'var(--live-red)' },
  cancelled:             { label: 'Cancelled',       color: 'var(--text-mut)' },
};

export default function MyMatchesPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const myUserId = useAuthStore((s) => s.userId);

  const [matches, setMatches] = useState<MyMatch[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedRoundId, setSelectedRoundId] = useState<string | null>(null);
  // Custom round-picker dropdown open state. Closed on outside click /
  // Escape via the effect below.
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    function onDocClick(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setPickerOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    setError(null);
    Promise.all([api.getMyMatches(eventId), api.getTournamentRounds(eventId)])
      .then(([res, rounds]) => {
        if (cancelled) return;
        const roundMap = new Map(rounds.map((round) => [round.id, round]));
        const list = (res.matches as MyMatch[]).map((match) => ({
          ...match,
          roundNumber: match.roundNumber ?? roundMap.get(match.roundId)?.roundNumber ?? 0,
          roundStatus: match.roundStatus ?? roundMap.get(match.roundId)?.status ?? 'draft',
        }));
        setMatches(list);
        // Auto-select the first round so the user always has SOMETHING
        // showing below the dropdown.
        if (list.length > 0) setSelectedRoundId(list[0].roundId);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message ?? "Couldn't load your matches");
      });
    return () => { cancelled = true; };
  }, [eventId]);

  // Group matches by round so the dropdown shows one option per round.
  // (Rare edge case: one user in two matches in the same round —
  // surface the first match for that round; the existing MyMatches
  // list on the event home covers the multi-match case.)
  const roundOptions = useMemo(() => {
    if (!matches) return [];
    const seen = new Map<string, MyMatch>();
    for (const m of matches) {
      if (!seen.has(m.roundId)) seen.set(m.roundId, m);
    }
    return Array.from(seen.values());
  }, [matches]);

  const selectedMatch = useMemo(
    () => roundOptions.find((m) => m.roundId === selectedRoundId) ?? null,
    [roundOptions, selectedRoundId],
  );

  // Stable "you / partner vs opponents" labels based on which side
  // the signed-in user is on.
  const sideLabels = useMemo(() => {
    if (!selectedMatch || !myUserId) return null;
    const me = selectedMatch.participants.find((p) => p.userId === myUserId);
    const mySideNumber = me?.sideNumber ?? 1;
    const myTeam   = selectedMatch.participants.filter((p) => p.sideNumber === mySideNumber);
    const opp      = selectedMatch.participants.filter((p) => p.sideNumber !== mySideNumber);
    return {
      mine: sideSummary(myTeam, 'You'),
      opp:  sideSummary(opp, 'Opponent'),
    };
  }, [selectedMatch, myUserId]);

  const status = selectedMatch ? STATUS_LABEL[selectedMatch.status] : null;
  const formatLabel = selectedMatch ? (FORMAT_LABEL[selectedMatch.formatType] ?? selectedMatch.formatType) : null;
  const canEnterScores = Boolean(selectedMatch
    && ['locked', 'in_progress'].includes(selectedMatch.roundStatus)
    && !['pending_confirmation', 'disputed', 'final', 'cancelled'].includes(selectedMatch.status));

  return (
    <AppShell back={{ to: `/app/tournaments/${eventId}`, label: 'Event' }}>
      <section className="t-hero">
        <h1>My Match</h1>
        <p>Pick the round you're playing and jump straight into score entry.</p>
      </section>

      {error && <div className="t-error">{error}</div>}

      {matches && matches.length === 0 ? (
        <div className="t-empty">
          <div className="t-empty-title">You're not in any matches yet</div>
          <div className="t-empty-hint">
            Once your captain assigns you to a match, it'll show up here. Until then, ask your captain to add you to a pairing.
          </div>
        </div>
      ) : (
        <>
          {/* Round picker. Fully custom dropdown (not a native select) —
              the browser's option list renders OS chrome (blue highlight
              on macOS) that clashes with the dark theme. The menu is
              app-styled: card rows, green check on the active round. */}
          <div className="t-field" style={{ marginTop: 12 }}>
            <div className="t-label" id="mm-round-label">
              Round · tap to switch
            </div>
            <div ref={pickerRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className="t-input"
                aria-haspopup="listbox"
                aria-expanded={pickerOpen}
                aria-labelledby="mm-round-label"
                disabled={!matches}
                onClick={() => setPickerOpen((v) => !v)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {!matches
                    ? 'Loading…'
                    : selectedMatch
                      ? `${selectedMatch.roundName} · ${FORMAT_LABEL[selectedMatch.formatType] ?? selectedMatch.formatType}`
                      : 'Pick a round'}
                </span>
                <ChevronDown
                  width={18}
                  height={18}
                  aria-hidden
                  style={{
                    color: 'var(--green-bright)',
                    flexShrink: 0,
                    transform: pickerOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.15s ease',
                  }}
                />
              </button>

              {pickerOpen && matches && (
                <div
                  role="listbox"
                  aria-labelledby="mm-round-label"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 6px)',
                    left: 0,
                    right: 0,
                    zIndex: 30,
                    background: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: 12,
                    boxShadow: '0 18px 40px rgba(0,0,0,0.5)',
                    overflow: 'hidden',
                    maxHeight: '60vh',
                    overflowY: 'auto',
                  }}
                >
                  {roundOptions.map((m, i) => {
                    const active = m.roundId === selectedRoundId;
                    return (
                      <button
                        key={m.roundId}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => { setSelectedRoundId(m.roundId); setPickerOpen(false); }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 10,
                          width: '100%',
                          padding: '12px 14px',
                          minHeight: 48,
                          background: active ? 'rgba(34,197,94,0.10)' : 'transparent',
                          border: 'none',
                          borderTop: i > 0 ? '1px solid var(--border)' : 'none',
                          color: 'var(--text)',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'block', fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {m.roundName}
                          </span>
                          <span style={{ display: 'block', fontSize: 12, color: 'var(--text-mut)', marginTop: 2 }}>
                            {FORMAT_LABEL[m.formatType] ?? m.formatType}
                          </span>
                        </span>
                        {active && <Check width={16} height={16} style={{ color: 'var(--green-bright)', flexShrink: 0 }} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {selectedMatch && sideLabels && (
            <section
              style={{
                marginTop: 14,
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: 14,
                padding: 18,
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
              }}
            >
              {/* Match header */}
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--text)', letterSpacing: '-0.01em' }}>
                    {selectedMatch.roundName}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-sec)', fontWeight: 700, marginTop: 4 }}>
                    {formatLabel} · Match {selectedMatch.matchNumber}
                  </div>
                </div>
                {status && (
                  <span
                    style={{
                      fontSize: 11, fontWeight: 900, letterSpacing: 0.8, textTransform: 'uppercase',
                      color: status.color,
                      border: `1px solid ${status.color}`,
                      padding: '3px 8px', borderRadius: 4,
                    }}
                  >
                    {status.label}
                  </span>
                )}
              </div>

              {/* You + partner vs opponents */}
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: 14,
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--green-bright)', letterSpacing: 0.6, textTransform: 'uppercase' }}>
                    Your side
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {sideLabels.mine}
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 900, color: 'var(--text-mut)', letterSpacing: 1 }}>VS</span>
                <div style={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-sec)', letterSpacing: 0.6, textTransform: 'uppercase' }}>
                    Opponents
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {sideLabels.opp}
                  </div>
                </div>
              </div>

              {selectedMatch.finalResultDisplay && (
                <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--green-bright)', textAlign: 'center', letterSpacing: 0.4 }}>
                  Final result: {selectedMatch.finalResultDisplay}
                </div>
              )}

              {!canEnterScores && !selectedMatch.finalResultDisplay && (
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold)', textAlign: 'center' }}>
                  Score entry opens after this round is locked for scoring.
                </div>
              )}

              {/* Primary CTA */}
              <button
                type="button"
                onClick={() => navigate(`/app/tournament-matches/${selectedMatch.matchId}/score`)}
                className="t-btn t-btn-primary t-btn-block"
                style={{ marginTop: 4 }}
              >
                <Play width={14} height={14} /> {canEnterScores ? 'Enter Scores' : 'View Match'}
              </button>

              <Link
                to={`/app/tournaments/${eventId}/rounds/${selectedMatch.roundId}`}
                className="t-hint"
                style={{ textAlign: 'center', textDecoration: 'none', color: 'var(--text-sec)' }}
              >
                <Users width={12} height={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                View full round details
              </Link>
            </section>
          )}
        </>
      )}
    </AppShell>
  );
}
